/**
 * Pages Behavior Editor
 * Manages page-based layouts: page list, navigation, transitions, and autoplay config.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { SceneObject, TraceScene } from 'trace-viewer/scene';
import type { PagesData, PageData, PropertyDeclaration, StateDefinition } from 'trace-viewer/behaviors';
import { Pages, Properties, PAGE_STATE_ENTER, PAGE_STATE_EXIT } from 'trace-viewer/behaviors';

import { StateDetailEditor, ValueEditor } from './state-ui';
import { splitPageChildren } from 'trace-viewer/editor';
import { readPageOverride, pageSelfState } from './pageValuesModel';
import { setPageValueOverride, setPageValueBase } from '../page-values';
import { PAGE_PRESETS, applyPreset as applyPagePreset, applyDeckPreset, detectPreset as detectPresetFromBehavior, setSharedIntent } from 'trace-viewer/editor';
import type { PagePreset } from 'trace-viewer/editor';
import { pushCommand } from './pagesSharedActions';

const TRANSITION_PRESET_OPTIONS: { value: '' | PagePreset; label: string }[] = [
    { value: '', label: '— none —' },
    ...(Object.keys(PAGE_PRESETS) as PagePreset[]).map(key => ({ value: key, label: PAGE_PRESETS[key].label })),
];

// ============================================================================
// Styles
// ============================================================================

const styles = {
    navBar: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        marginBottom: '8px',
    },
    navButton: {
        padding: '6px 12px',
        backgroundColor: '#333',
        border: '1px solid #555',
        borderRadius: '4px',
        color: '#ccc',
        cursor: 'pointer',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '36px',
    } as React.CSSProperties,
    navLabel: {
        flex: 1,
        textAlign: 'center' as const,
        fontSize: '12px',
        color: '#aaa',
    },
    section: {
        marginTop: '10px',
        padding: '8px',
        backgroundColor: '#1e1e1e',
        borderRadius: '4px',
    },
    sectionHeader: {
        fontSize: '11px',
        color: '#888',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.5px',
        marginBottom: '6px',
    },
    select: {
        width: '100%',
        padding: '4px 8px',
        backgroundColor: '#333',
        border: '1px solid #555',
        borderRadius: '3px',
        color: '#fff',
        fontSize: '12px',
        cursor: 'pointer',
    } as React.CSSProperties,
    fieldRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '6px',
    },
    label: {
        fontSize: '12px',
        color: '#aaa',
        minWidth: '80px',
        flexShrink: 0,
    },
    input: {
        padding: '4px 8px',
        backgroundColor: '#333',
        border: '1px solid #555',
        borderRadius: '3px',
        color: '#fff',
        fontSize: '12px',
        outline: 'none',
    } as React.CSSProperties,
};

// ============================================================================

// ============================================================================
// Helpers
// ============================================================================

function getViewer() {
    return document.querySelector('trace-viewer');
}

function getTraceScene(): TraceScene | null {
    const viewer = getViewer();
    return viewer?.traceScene ?? null;
}


// ============================================================================
// Page States Section
// ============================================================================

/**
 * Detect preset for a target using the per-page assignments as source of truth.
 * Falls back to reading behavior data for the specific preset name.
 */
function detectPreset(target: SceneObject, pageConfigs: PageData[], container: SceneObject): string {
    const traceScene = getTraceScene();
    if (!traceScene) return '';
    const path = traceScene.getRelativePath(target, container);

    // Check if this target is assigned a state on any page
    const hasMapping = pageConfigs.some(p => p?.states?.some(a => a.target === path));
    if (!hasMapping) return '';

    // Has an assignment — read the specific preset from behavior data
    return detectPresetFromBehavior(target);
}

interface PageStatesSectionProps {
    data: PagesData;
    pages: { name: string; sceneObject: SceneObject }[];
    container: SceneObject;
    /** The selected object — null = container, SceneObject = a page or child */
    selectedObject: SceneObject | null;
    onChange: (data: PagesData) => void;
}

const PageStatesSection: React.FC<PageStatesSectionProps> = ({ data, pages, container, selectedObject, onChange }) => {
    const [statesExpanded, setStatesExpanded] = useState(false);

    const duration = data.transition?.duration ?? 0.5;
    const easing = data.transition?.easing ?? 'easeInOut';

    const traceScene = getTraceScene();
    const pageConfigs = data.pages ?? [];

    const target = selectedObject;
    const targetPath = target && traceScene ? traceScene.getRelativePath(target, container) : null;

    // Detect current preset using the per-page assignments as source of truth.
    // This avoids stale reads from async behavior commits.
    let currentPreset = '';
    if (target) {
        currentPreset = detectPreset(target, pageConfigs, container);
    } else {
        for (const page of pages) {
            if (page.sceneObject) {
                const p = detectPreset(page.sceneObject, pageConfigs, container);
                if (p) { currentPreset = p; break; }
            }
        }
    }

    /** Build an updated pages array with an object's state assignments.
     *  Home page: no assignment (reverts to authored via setState(undefined)).
     *  Other pages: _pageExit (offscreen).
     *  The runtime snaps incoming objects to _pageEnter before reverting to base. */
    const buildPagesForObject = (
        existing: PageData[],
        path: string,
        preset: string | null,
    ): PageData[] => {
        // Clone, positionally sized to the page list.
        const updated: PageData[] = [];
        for (let i = 0; i < Math.max(existing.length, pages.length); i++) {
            updated.push({ ...existing[i], states: existing[i]?.states?.map(a => ({ ...a })) });
        }
        const removeFrom = (i: number): void => {
            const states = updated[i].states?.filter(a => a.target !== path);
            updated[i].states = states && states.length > 0 ? states : undefined;
        };
        if (preset) {
            const segments = path.replace(/^\.\//, '').split('/');
            for (let i = 0; i < pages.length; i++) {
                if (pages[i].name === segments[0]) {
                    // Home page: no assignment so setState(undefined) reverts to authored
                    removeFrom(i);
                } else {
                    const states = (updated[i].states ?? []).filter(a => a.target !== path);
                    states.push({ target: path, state: PAGE_STATE_EXIT });
                    updated[i].states = states;
                }
            }
        } else {
            for (let i = 0; i < updated.length; i++) removeFrom(i);
        }
        // Trim trailing pages with no assignments.
        while (updated.length > 0 && !updated[updated.length - 1].states) updated.pop();
        return updated;
    };

    /** Apply a preset to a single target, or to every page (container default). */
    const applyPreset = async (obj: SceneObject, preset: '' | PagePreset) => {
        pushCommand(await applyPagePreset(container, obj, preset || null));
    };
    const applyDefaultPreset = async (preset: '' | PagePreset) => {
        pushCommand(await applyDeckPreset(container, preset || null));
    };

    // Gather property names and defs from target's Properties behavior
    const props = target?.getBehavior(Properties);
    const propsData = props?.getData();
    const propertyNames: string[] = [];
    const propertyDefs = new Map<string, PropertyDeclaration>();
    if (propsData?.properties) {
        for (const decl of propsData.properties) {
            propertyNames.push(decl.name);
            propertyDefs.set(decl.name, decl);
        }
    }
    const stateData = propsData?.states;
    const enterState = stateData?.[PAGE_STATE_ENTER];
    const exitState = stateData?.[PAGE_STATE_EXIT];
    const hasStates = !!(enterState || exitState);

    /** Commit updated state back to the target's Properties behavior.
     *  Creates Properties + states if they don't exist yet. */
    const commitState = async (stateName: string, updated: StateDefinition) => {
        if (!target) return;
        let p = props;
        if (!p) {
            p = await target.addBehavior(Properties);
        }
        const d = p.getData();
        const newStates = { ...d.states, [stateName]: updated };
        void p.commit({ ...d, states: newStates });

        // Ensure the per-page assignments include this target
        if (targetPath) {
            const updated2 = buildPagesForObject(pageConfigs, targetPath, 'custom');
            onChange({ ...data, pages: updated2.length > 0 ? updated2 : undefined });
        }
    };

    /** Create empty _pageEnter and _pageExit states on the target. */
    const createEmptyStates = async () => {
        if (!target) return;
        let p = props;
        if (!p) {
            p = await target.addBehavior(Properties);
        }
        const d = p.getData();
        const newStates = {
            ...d.states,
            [PAGE_STATE_ENTER]: { values: {}, transform: {} },
            [PAGE_STATE_EXIT]: { values: {}, transform: {} },
        };
        void p.commit({ ...d, states: newStates });

        if (targetPath) {
            const updated = buildPagesForObject(pageConfigs, targetPath, 'custom');
            onChange({ ...data, pages: updated.length > 0 ? updated : undefined });
        }
    };

    return (
        <div style={{ ...styles.section, borderLeft: !target ? '2px solid #5a8' : '2px solid transparent', paddingLeft: '10px' }}>
            <div style={styles.sectionHeader}>
                Transition{target ? ` — ${target.name}` : ' — All Pages'}
            </div>

            {/* Duration + Easing — only shown at container level (no target selected) */}
            {!target && (
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '8px' }}>
                    <label style={{ fontSize: '11px', color: '#777' }}>Duration</label>
                    <input
                        type="number" value={duration}
                        onChange={(e) => { const v = parseFloat(e.target.value); onChange({ ...data, transition: { ...data.transition, duration: Number.isFinite(v) ? Math.max(0, v) : 0.5 } }); }}
                        step={0.1} min={0}
                        style={{ ...styles.input, width: '50px', fontSize: '10px', padding: '2px 4px' }}
                    />
                    <span style={{ fontSize: '10px', color: '#555' }}>s</span>
                    <select value={easing} onChange={(e) => onChange({ ...data, transition: { duration, ...data.transition, easing: e.target.value as typeof easing } })}
                        style={{ ...styles.input, fontSize: '10px', padding: '2px 4px', cursor: 'pointer' }}>
                        <option value="linear">linear</option>
                        <option value="easeIn">easeIn</option>
                        <option value="easeOut">easeOut</option>
                        <option value="easeInOut">easeInOut</option>
                    </select>
                </div>
            )}

            {/* Preset dropdown */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                <label style={{ fontSize: '11px', color: '#777', minWidth: '50px' }}>Preset</label>
                <select
                    value={currentPreset}
                    onChange={(e) => {
                        const preset = TRANSITION_PRESET_OPTIONS.find(o => o.value === e.target.value)?.value ?? '';
                        if (target) {
                            void applyPreset(target, preset);
                        } else {
                            void applyDefaultPreset(preset);
                        }
                    }}
                    style={{ ...styles.input, flex: 1, fontSize: '11px', padding: '3px 6px', cursor: 'pointer', color: currentPreset ? '#ccc' : '#555' }}
                >
                    {TRANSITION_PRESET_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
            </div>

            {!target && (
                <div style={{ fontSize: '10px', color: '#555', fontStyle: 'italic', marginTop: '4px' }}>
                    Applies to all pages without overrides. Select a page or child for per-object control.
                </div>
            )}

            {/* Full state editor — expandable */}
            {target && (
                <div style={{ marginTop: '8px' }}>
                    <div
                        style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', marginBottom: statesExpanded ? '4px' : 0 }}
                        onClick={() => setStatesExpanded(!statesExpanded)}
                    >
                        <span style={{ fontSize: '8px', color: '#666', width: '10px' }}>{statesExpanded ? '▼' : '▶'}</span>
                        <span style={{ fontSize: '11px', color: '#888' }}>States</span>
                        {hasStates && (
                            <span style={{ fontSize: '9px', color: '#555' }}>
                                {[enterState && PAGE_STATE_ENTER, exitState && PAGE_STATE_EXIT].filter(Boolean).join(', ')}
                            </span>
                        )}
                    </div>
                    {statesExpanded && (
                        <div>
                            {[
                                { key: PAGE_STATE_ENTER, label: '_pageEnter', state: enterState },
                                { key: PAGE_STATE_EXIT, label: '_pageExit', state: exitState },
                            ].map(({ key, label, state: s }) => s && (
                                <div key={key} style={{
                                    marginBottom: '4px', backgroundColor: '#1e1e1e', borderRadius: '4px',
                                    border: `1px solid ${s.transition ? '#584' : '#333'}`,
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 8px' }}>
                                        <span style={{ fontSize: '11px', color: '#8af', fontFamily: 'monospace', flex: 1 }}>
                                            {label}
                                        </span>
                                        {s.transition && (
                                            <span
                                                style={{ fontSize: '8px', color: '#584', border: '1px solid #584', borderRadius: '2px', padding: '0 3px', cursor: 'pointer' }}
                                                title="This state overrides the Pages-level transition. Click to remove override."
                                                onClick={() => {
                                                    const { transition: _, ...rest } = s;
                                                    void commitState(key, rest);
                                                }}
                                            >override</span>
                                        )}
                                        <span style={{ fontSize: '9px', color: '#555' }}>
                                            {Object.keys(s.values).length}v
                                            {s.transform ? ' +t' : ''}
                                        </span>
                                    </div>
                                    <StateDetailEditor
                                        state={s}
                                        stateName={key}
                                        propertyNames={propertyNames}
                                        propertyDefs={propertyDefs}
                                        onChange={(updated) => void commitState(key, updated)}
                                    />
                                </div>
                            ))}
                            {!hasStates && (
                                <button
                                    onClick={() => void createEmptyStates()}
                                    style={{
                                        width: '100%', padding: '6px', backgroundColor: '#383838',
                                        border: '1px dashed #555', borderRadius: '4px', color: '#aaa',
                                        cursor: 'pointer', fontSize: '11px',
                                    }}
                                >+ Create enter/exit states</button>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};


// ============================================================================
// Pages Editor Component
// ============================================================================


// ============================================================================
// Page values — "page selection drives a property"
// ============================================================================

interface PageValuesSectionProps {
    container: SceneObject;
    data: PagesData;
    pages: { name: string }[];
    activeIndex: number;
}

/**
 * The container's own declared properties, each with its base value and the
 * override the active page holds (blank = inherits the base). Overrides live
 * as a self-targeted page state on the container — the shape the legacy
 * FollowActive compile emits — so a desk group bound to `{deckHeight}` sits
 * where the ESPN stage page says.
 */
const PageValuesSection: React.FC<PageValuesSectionProps> = ({ container, data, pages, activeIndex }) => {
    const props = container.getBehavior(Properties);
    const propsData = props?.getData();
    const declarations = propsData?.properties ?? [];
    if (!propsData || declarations.length === 0) return null;
    const page = pages[activeIndex];
    const stateName = pageSelfState(data.pages, activeIndex);

    return (
        <div style={styles.section}>
            <div style={styles.sectionHeader}>
                Page values{page ? ` — ${page.name}` : ''}
                {stateName && <span style={{ color: '#666', fontWeight: 400 }}> · {stateName}</span>}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(60px, 1fr) 84px 84px 18px', gap: '4px 6px', alignItems: 'center' }}>
                <span style={{ fontSize: '10px', color: '#666' }} />
                <span style={{ fontSize: '10px', color: '#666' }}>Base</span>
                <span style={{ fontSize: '10px', color: '#666' }}>This page</span>
                <span />
                {declarations.map(decl => {
                    const range = decl.type === 'number' ? { min: decl.min, max: decl.max, step: decl.step } : {};
                    const override = page ? readPageOverride(propsData, data.pages, activeIndex, decl.name) : undefined;
                    return (
                        <React.Fragment key={decl.name}>
                            <span style={{ ...styles.label, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }} title={decl.name}>
                                {decl.label ?? decl.name}
                            </span>
                            <ValueEditor
                                type={decl.type}
                                value={decl.value}
                                onChange={(v) => { void setPageValueBase(container, decl.name, v); }}
                                {...range}
                            />
                            {override !== undefined ? (
                                <ValueEditor
                                    type={decl.type}
                                    value={override}
                                    onChange={(v) => { void setPageValueOverride(container, activeIndex, decl.name, v); }}
                                    {...range}
                                />
                            ) : (
                                <button
                                    style={{ ...styles.input, color: '#888', cursor: page ? 'pointer' : 'not-allowed', textAlign: 'left' }}
                                    disabled={!page}
                                    title={page ? `Override ${decl.label ?? decl.name} on ${page.name}` : 'Select a page'}
                                    onClick={() => { void setPageValueOverride(container, activeIndex, decl.name, decl.value); }}
                                >
                                    inherit
                                </button>
                            )}
                            {override !== undefined ? (
                                <button
                                    style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: '11px', padding: 0 }}
                                    title="Remove this page's override"
                                    onClick={() => { void setPageValueOverride(container, activeIndex, decl.name, null); }}
                                >
                                    ✕
                                </button>
                            ) : <span />}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
};

// ============================================================================
// PagesEditor
// ============================================================================

export interface PagesEditorProps {
    data: PagesData;
    behavior: Pages;
    onChange: (data: PagesData) => void;
}

export const PagesEditor: React.FC<PagesEditorProps> = ({ data, behavior, onChange }) => {
    const container = behavior.sceneObject;
    const [pages, setPages] = useState<{ name: string; sceneObject: SceneObject }[]>([]);
    const [activeIndex, setActiveIndex] = useState<number>(-1);
    const [isAnimating, setIsAnimating] = useState(false);
    const [showAll, setShowAll] = useState(false);
    /** The SCENE selection when it sits inside the container (else null) —
     *  drives per-object transition editing. The panel no longer tracks its
     *  own selection: page management lives in the strip; one selection, one
     *  context. */
    const [sceneTarget, setSceneTarget] = useState<SceneObject | null>(null);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const isNested = data.pageSource === 'firstChild';

    /** Container children split into page children and shared children, per
     *  the current `data.shared` (same fail-safe as the runtime: unresolvable
     *  paths exclude nothing). */
    const splitChildren = useCallback(() => {
        const traceScene = getTraceScene();
        return splitPageChildren(
            container.getChildren(),
            data.shared,
            (path) => traceScene?.resolveSceneObject(path, container) ?? null,
        );
    }, [container, data.shared]);

    const refreshPages = useCallback(() => {
        if (isNested) {
            const nodes = behavior.getPageNodes();
            setPages(nodes.map(node => ({ name: node.name, sceneObject: null as unknown as SceneObject })));
        } else {
            const split = splitChildren();
            setPages(split.pageChildren.map(child => ({ name: child.name, sceneObject: child })));
        }
        setActiveIndex(behavior.getActivePageIndex());
        // Transition target = the scene selection when it's inside the
        // container (a page, a page child, or shared content). Container
        // itself → null (edit the all-pages defaults).
        const sel = getTraceScene()?.getSelectedObject?.() ?? null;
        let target: SceneObject | null = null;
        if (sel && sel !== container) {
            let cur: SceneObject | null = sel;
            while (cur) {
                if (cur === container) { target = sel; break; }
                cur = cur.getParent();
            }
        }
        setSceneTarget(target);
    }, [isNested, behavior, container, splitChildren]);

    // Poll for changes
    useEffect(() => {
        refreshPages();
        pollRef.current = setInterval(refreshPages, 500);
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, [refreshPages]);

    const reselectContainer = useCallback(() => {
        const traceScene = getTraceScene();
        if (traceScene) traceScene.select(container);
    }, [container]);

    const toggleShowAll = useCallback((on: boolean) => {
        setShowAll(on);
        if (on) {
            behavior.showAllPages();
        } else {
            behavior.showPage(activeIndex, true);
        }
    }, [behavior, activeIndex]);

    const navigatePrev = useCallback(async () => {
        if (pages.length < 2 || isAnimating) return;
        if (showAll) { setShowAll(false); setSharedIntent(container, false); }
        const newIndex = activeIndex <= 0 ? pages.length - 1 : activeIndex - 1;
        setIsAnimating(true);
        try {
            // showPage is fire-and-forget under the states engine (it drives the
            // transition on its own timer); the button stays briefly disabled via
            // the guard below rather than awaiting completion.
            behavior.showPage(newIndex, true);
            setActiveIndex(newIndex);
            reselectContainer();
        } finally {
            setIsAnimating(false);
        }
    }, [pages.length, activeIndex, isAnimating, showAll, behavior, container, reselectContainer]);

    const navigateNext = useCallback(async () => {
        if (pages.length < 2 || isAnimating) return;
        if (showAll) { setShowAll(false); setSharedIntent(container, false); }
        const newIndex = activeIndex >= pages.length - 1 ? 0 : activeIndex + 1;
        setIsAnimating(true);
        try {
            behavior.showPage(newIndex, true);
            setActiveIndex(newIndex);
            reselectContainer();
        } finally {
            setIsAnimating(false);
        }
    }, [pages.length, activeIndex, isAnimating, showAll, behavior, container, reselectContainer]);

    // ---- Render ----
    // Page MANAGEMENT (add/rename/duplicate/delete/reorder/select) lives in
    // the Pages strip — this panel keeps navigation + the show-all overview,
    // transitions (per-object via the scene selection), options, and autoplay.

    return (
        <div>
            {/* Navigation bar */}
            <div style={styles.navBar}>
                <button
                    style={{ ...styles.navButton, opacity: isAnimating || pages.length < 2 ? 0.5 : 1, cursor: isAnimating || pages.length < 2 ? 'not-allowed' : 'pointer' }}
                    onClick={navigatePrev}
                    disabled={isAnimating || pages.length < 2}
                    title="Previous page"
                >
                    ◀
                </button>
                <span style={styles.navLabel}>
                    {pages.length > 0 ? `${activeIndex + 1} / ${pages.length}` : '0 / 0'}
                </span>
                <button
                    style={{ ...styles.navButton, opacity: isAnimating || pages.length < 2 ? 0.5 : 1, cursor: isAnimating || pages.length < 2 ? 'not-allowed' : 'pointer' }}
                    onClick={navigateNext}
                    disabled={isAnimating || pages.length < 2}
                    title="Next page"
                >
                    ▶
                </button>
                {pages.length >= 2 && (
                    <button
                        style={{ ...styles.navButton, fontSize: '11px', minWidth: '28px', backgroundColor: showAll ? '#4a6a4a' : '#333' }}
                        onClick={() => toggleShowAll(!showAll)}
                        title={showAll ? 'Show one page at a time' : 'Show all pages'}
                    >
                        All
                    </button>
                )}
            </div>

            {/* ── Page values: the container's own properties, per page ── */}
            {!isNested && pages.length >= 1 && (
                <PageValuesSection
                    container={container}
                    data={data}
                    pages={pages}
                    activeIndex={activeIndex}
                />
            )}

            {/* ── Page States Section ── */}
            {!isNested && pages.length >= 2 && (
                <PageStatesSection
                    data={data}
                    pages={pages}
                    container={container}
                    selectedObject={sceneTarget}
                    onChange={onChange}
                />
            )}

            {/* Options */}
            <div style={styles.section}>
                <div style={styles.sectionHeader}>Options</div>
                <div style={styles.fieldRow}>
                    <label style={styles.label}>Nested Pages</label>
                    <input
                        type="checkbox"
                        checked={data.pageSource === 'firstChild'}
                        onChange={(e) => onChange({ ...data, pageSource: e.target.checked ? 'firstChild' : undefined })}
                    />
                </div>
                {/* Tap anywhere on the deck's content advances one page in
                    play mode; taps claimed by an object's own interaction
                    (collider, graph, states) still fire that instead. */}
                <div style={styles.fieldRow}>
                    <label style={styles.label}>Advance on Tap</label>
                    <input
                        type="checkbox"
                        checked={data.advanceOnTap ?? false}
                        onChange={(e) => onChange({ ...data, advanceOnTap: e.target.checked ? true : undefined })}
                    />
                </div>
                {/* One deck-level control, deliberately outside Autoplay: the
                    field governs tap-advance wrapping and autoplay alike, so
                    a second checkbox under Autoplay would silently edit this
                    same value. Writes `loop` (trace-viewer#642); the legacy
                    `autoplayLoop` is only read, for a deck authored before its
                    first commit migrates — commit erases the alias, so reading
                    it alone would show this box unticked on a looping deck. */}
                <div style={styles.fieldRow}>
                    <label style={styles.label}>Loop</label>
                    <input
                        type="checkbox"
                        checked={data.loop ?? data.autoplayLoop ?? false}
                        onChange={(e) => onChange({ ...data, loop: e.target.checked })}
                    />
                </div>
            </div>

            {/* Autoplay config */}
            <div style={styles.section}>
                <div style={styles.sectionHeader}>Autoplay</div>
                <div style={styles.fieldRow}>
                    <label style={styles.label}>Enabled</label>
                    <input
                        type="checkbox"
                        checked={data.autoplay ?? false}
                        onChange={(e) => onChange({ ...data, autoplay: e.target.checked })}
                    />
                </div>
                {data.autoplay && (
                    <>
                        <div style={styles.fieldRow}>
                            <label style={styles.label}>Duration (s)</label>
                            <input
                                type="number"
                                value={data.pageDuration ?? 5}
                                onChange={(e) => onChange({ ...data, pageDuration: parseFloat(e.target.value) || 5 })}
                                step={0.5}
                                min={0.5}
                                style={{ ...styles.input, width: '70px' }}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
